import React, { useState, useEffect, useRef } from 'react';
import { websocketService, WebSocketMessage, ProgressData } from '../services/websocketService';
import {GameSession} from "./GameInitializer";
import TypewriterText from './TypewriterText';
import { CookieManager } from '../utils/cookieManager';
import { useWebSocket } from '../contexts/WebSocketContext';
import logger from '../utils/debugLogger';

interface GameInterfaceProps {
  gameSession: GameSession;
  onNewGame: () => void;
}

interface GameHistoryEntry {
  type: 'action' | 'response' | 'progress';
  content: string;
  imageUrl?: string;
  sceneDescription?: string;
  isTyping?: boolean;
  progress?: ProgressData;
}

type GameStatus = '<---PLAYER_DEATH--->' | '<---QUEST_FAILED--->' | '<---QUEST_COMPLETE--->' | '<---QUEST_IN_PROGRESS--->' | null;

const GameInterface: React.FC<GameInterfaceProps> = ({ gameSession: initialGameSession, onNewGame }) => {
  logger.info('GameInterface - Received initial game session:', initialGameSession);
  logger.info('GameInterface - Primers:', initialGameSession.primers);
  
  const [action, setAction] = useState('');
  const [gameHistory, setGameHistory] = useState<GameHistoryEntry[]>([
    { type: 'response', content: initialGameSession.currentScene }
  ]);
  const [loading, setLoading] = useState(false);
  const [currentGameSession, setCurrentGameSession] = useState(initialGameSession);
  const [currentImageUrl, setCurrentImageUrl] = useState(initialGameSession.imageUrl);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [gameStatus, setGameStatus] = useState<GameStatus>(null);
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  
  // Use the WebSocket context
  const { isConnected: wsConnected } = useWebSocket();

  // Subscribe to messages
  useEffect(() => {
    const unsubscribeMessage = websocketService.onMessage((message: WebSocketMessage) => {
      handleWebSocketMessage(message);
    });

    // Cleanup on unmount
    return () => {
      unsubscribeMessage();
    };
  }, []);

  const handleWebSocketMessage = (message: WebSocketMessage) => {
    logger.debug('Received WebSocket message:', logger.sanitize(message));

    switch (message.type) {
      case 'game_init_started':
        setProgressMessage(message.data.message || 'Initializing game...');
        break;

      case 'progress':
        const progress = message.data as ProgressData;
        setProgressMessage(progress.message);
        break;

      case 'game_ready':
        setProgressMessage('');
        setLoading(false);
        // Handle initial game data if this is a new game
        if (message.data.narrative) {
          setCurrentGameSession(prev => ({
            ...prev,
            currentScene: message.data.narrative,
            sceneDescription: message.data.sceneDescription,
            imageUrl: message.data.imageUrl,
            gameId: message.gameId || prev.gameId
          }));
          setCurrentImageUrl(message.data.imageUrl);
        }
        break;

      case 'action_processing':
        setProgressMessage(message.data.message || 'Processing action...');
        break;

      case 'action_result':
        setProgressMessage('');
        setLoading(false);
        
        // Update game status if provided
        if (message.data.gameStatus) {
          setGameStatus(message.data.gameStatus);
          
          // Remove cookie if game is over
          if (message.data.gameStatus !== '<---QUEST_IN_PROGRESS--->' && 
              message.data.gameStatus !== null) {
            CookieManager.removeGameSession(currentGameSession.gameId);
          }
        }
        
        // Update current image if a new one was generated
        if (message.data.imageUrl) {
          setCurrentImageUrl(message.data.imageUrl);
        }
        
        // Add response to history with typing effect
        setGameHistory(prev => [...prev, { 
          type: 'response', 
          content: message.data.narrative,
          imageUrl: message.data.imageUrl,
          sceneDescription: message.data.sceneDescription,
          isTyping: true
        }]);
        
        // Update game session state
        setCurrentGameSession(prev => ({
          ...prev,
          currentScene: message.data.narrative,
          sceneDescription: message.data.sceneDescription,
          imageUrl: message.data.imageUrl,
          isComplete: message.data.gameStatus !== 'QUEST_IN_PROGRESS' && message.data.gameStatus != null
        }));
        break;

      case 'error':
        setProgressMessage('');
        setLoading(false);
        setGameHistory(prev => [...prev, { 
          type: 'response', 
          content: `Error: ${message.data.message}` 
        }]);
        break;
    }
  };

  // Auto-scroll to bottom when new messages are added or during typing
  useEffect(() => {
    scrollToBottom();
  }, [gameHistory, progressMessage]);

  // Improved scroll to bottom function
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
      });
    }
  };

  const handleTypingComplete = (index: number) => {
    setGameHistory(prev => prev.map((entry, i) => 
      i === index ? { ...entry, isTyping: false } : entry
    ));
  };

  const handleActionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!action.trim() || !wsConnected || isGameOver()) return;

    setLoading(true);
    
    // Add user action to history
    setGameHistory(prev => [...prev, { 
      type: 'action', 
      content: action 
    }]);
    const currentAction = action;
    setAction('');

    try {
      // Update last played time in cookie
      CookieManager.updateLastPlayed(currentGameSession.gameId);
      
      // Send action via WebSocket
      await websocketService.sendAction(currentGameSession.gameId, currentAction);
    } catch (error) {
      logger.error('Error sending action:', error);
      setLoading(false);
      setGameHistory(prev => [...prev, { 
        type: 'response', 
        content: 'Failed to send action. Please check your connection and try again.' 
      }]);
    }
  };

  const isGameOver = () => {
    return gameStatus === '<---PLAYER_DEATH--->' ||
           gameStatus === '<---QUEST_FAILED--->' ||
           gameStatus === '<---QUEST_COMPLETE--->';
  };

  const getGameOverMessage = () => {
    switch (gameStatus) {
      case '<---PLAYER_DEATH--->':
        return { title: '💀 You Have Died', message: 'Your journey has come to an untimely end.' };
      case '<---QUEST_FAILED--->':
        return { title: '❌ Quest Failed', message: 'You were unable to complete your quest.' };
      case '<---QUEST_COMPLETE--->':
        return { title: '🎉 Quest Complete!', message: 'Congratulations! You have completed your adventure.' };
      default:
        return null;
    }
  };

  const connectionStatus = wsConnected ? 'Connected' : 'Disconnected';
  const connectionClass = wsConnected ? 'connected' : 'disconnected';
  
  // Log connection status changes for debugging
  useEffect(() => {
    logger.info(`GameInterface - WebSocket connection status changed: ${connectionStatus}`);
  }, [connectionStatus]);

  const handleImageClick = () => {
    setIsImageFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsImageFullscreen(false);
  };

  // Close fullscreen on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isImageFullscreen) {
        setIsImageFullscreen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isImageFullscreen]);

  return (
    <div className="game-interface">
      <div className="game-header">
        <h2>Unchained</h2>
        <div className="connection-status">
          <span className={`status-indicator ${connectionClass}`}></span>
          <span>{connectionStatus}</span>
        </div>
        <div className="primer-display">
          <strong>Your Words:</strong> {currentGameSession.primers.join(', ')}
        </div>
        <button onClick={onNewGame} className="new-game-btn">New Game</button>
      </div>

      <div className="game-content">
        <div className="scene-image">
          
          {currentImageUrl ? (
            <div className="image-container" onClick={handleImageClick}>
              <img 
                src={currentImageUrl} 
                alt={currentGameSession.sceneDescription || 'Current scene'}
                className="scene-img"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="tap-hint mobile-only">Tap to enlarge</div>
            </div>
          ) : (
            <div className="image-placeholder">
              <p>Scene Image</p>
              <small>{currentGameSession.sceneDescription}</small>
            </div>
          )}
        </div>

        <div className="game-chat">
          <div className="chat-messages" ref={chatMessagesRef}>
            {gameHistory.map((entry, index) => (
              <div key={index} className={`chat-message ${entry.type}`}>
                {entry.type === 'action' && <span className="message-prefix">You: </span>}
                {entry.type === 'response' && entry.isTyping ? (
                  <TypewriterText 
                    text={entry.content} 
                    speed={20}
                    onComplete={() => handleTypingComplete(index)}
                    onCharacterTyped={scrollToBottom}
                  />
                ) : (
                  entry.content
                )}
              </div>
            ))}
            {progressMessage && (
              <div className="progress-message">
                <div className="spinner"></div>
                {progressMessage}
              </div>
            )}
            {loading && !progressMessage && <div className="loading">Processing...</div>}
            {isGameOver() && getGameOverMessage() && (
              <div className={`game-over ${gameStatus?.toLowerCase()}`}>
                <h3>{getGameOverMessage()!.title}</h3>
                <p>{getGameOverMessage()!.message}</p>
                <button onClick={onNewGame} className="new-game-btn">Start New Adventure</button>
              </div>
            )}
          </div>

          <form onSubmit={handleActionSubmit} className="action-form">
            <input
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder={
                isGameOver() ? "Game Over - Start a new adventure" : 
                wsConnected ? "What do you do?" : 
                "Connecting..."
              }
              disabled={loading || !wsConnected || isGameOver()}
            />
            <button type="submit" disabled={loading || !action.trim() || !wsConnected || isGameOver()}>
              Act
            </button>
          </form>
        </div>
      </div>

      {/* Fullscreen Image Overlay */}
      {isImageFullscreen && currentImageUrl && (
        <div className="fullscreen-overlay" onClick={handleCloseFullscreen}>
          <div className="fullscreen-image-container">
            <img 
              src={currentImageUrl} 
              alt={currentGameSession.sceneDescription || 'Current scene'}
              className="fullscreen-img"
            />
            <button className="close-fullscreen" onClick={handleCloseFullscreen}>
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameInterface;